import { useEffect, useRef, useState, useCallback } from "react";

const WS_BASE = process.env.REACT_APP_BACKEND_URL
  ?.replace("https://", "wss://")
  ?.replace("http://", "ws://");

export const useWebSocket = (boardId, user) => {
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const [connected, setConnected] = useState(false);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [activities, setActivities] = useState([]);
  const handlersRef = useRef({});

  const connect = useCallback(() => {
    if (!boardId || !user) return;
    const token = localStorage.getItem("usg_token");
    if (!token) return;

    const url = `${WS_BASE}/api/ws/${boardId}?token=${token}`;

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnected(true);
        if (reconnectTimer.current) {
          clearTimeout(reconnectTimer.current);
          reconnectTimer.current = null;
        }
      };

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);

          if (msg.type === "presence") {
            setOnlineUsers(msg.users || []);
          } else if (msg.type === "activity") {
            setActivities((prev) => [...prev.slice(-49), msg.activity]);
          } else if (msg.type === "activity_history") {
            setActivities(msg.activities || []);
          } else if (msg.type === "decision_update") {
            handlersRef.current.onDecisionUpdate?.(msg);
          } else if (msg.type === "lock_update") {
            handlersRef.current.onLockUpdate?.(msg);
          } else if (msg.type === "timer_update") {
            handlersRef.current.onTimerUpdate?.(msg);
          } else if (msg.type === "vote_cast") {
            handlersRef.current.onVoteCast?.(msg);
          } else if (msg.type === "votes_revealed") {
            handlersRef.current.onVotesRevealed?.(msg);
          } else if (msg.type === "game_status_changed") {
            handlersRef.current.onGameStatusChanged?.(msg);
          } else if (msg.type === "disruption_phase_changed") {
            handlersRef.current.onDisruptionPhaseChanged?.(msg);
          } else if (msg.type === "spotlight") {
            handlersRef.current.onSpotlight?.(msg);
          } else if (msg.type === "spotlight_clear") {
            handlersRef.current.onSpotlightClear?.();
          }
        } catch {
          // ignore parse errors
        }
      };

      ws.onclose = () => {
        setConnected(false);
        wsRef.current = null;
        // Reconnect after 3s
        reconnectTimer.current = setTimeout(() => connect(), 3000);
      };

      ws.onerror = () => {
        ws.close();
      };
    } catch {
      reconnectTimer.current = setTimeout(() => connect(), 3000);
    }
  }, [boardId, user]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      if (wsRef.current) {
        wsRef.current.onclose = null; // prevent reconnect on intentional close
        wsRef.current.close();
      }
    };
  }, [connect]);

  const sendMessage = useCallback((msg) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(msg));
    }
  }, []);

  const sendViewing = useCallback((department) => {
    sendMessage({ type: "viewing", department });
  }, [sendMessage]);

  const sendTimerSync = useCallback((action, seconds) => {
    sendMessage({ type: "timer_sync", action, seconds });
  }, [sendMessage]);

  const sendSpotlight = useCallback((targetType, deptIdx, deptName, stallId, stallName) => {
    sendMessage({ type: "spotlight", target_type: targetType, department_idx: deptIdx, department_name: deptName, stall_id: stallId, stall_name: stallName });
  }, [sendMessage]);

  const sendSpotlightClear = useCallback(() => {
    sendMessage({ type: "spotlight_clear" });
  }, [sendMessage]);

  const registerHandlers = useCallback((handlers) => {
    handlersRef.current = handlers;
  }, []);

  return {
    connected,
    onlineUsers,
    activities,
    sendViewing,
    sendTimerSync,
    sendSpotlight,
    sendSpotlightClear,
    registerHandlers,
  };
};
